import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useRedditAuth } from './RedditAuthContext';
import './reddit.css';
import './RedditLogin.css';

const SLIDES = [
  {
    id: 'discover',
    kicker: 'Discover',
    title: 'Find conversations',
    body: 'See the threads where your brand belongs and which communities are worth the work.',
    image: '/reddit-login-discover.jpg',
  },
  {
    id: 'campaigns',
    kicker: 'Manage',
    title: 'Run campaigns',
    body: 'Keep assignments, progress, and the work still open in one campaign view.',
    image: '/reddit-login-campaigns.jpg',
  },
  {
    id: 'commenters',
    kicker: 'Coordinate',
    title: 'Work with commenters',
    body: 'Give the team a shared view of who is commenting and what still needs attention.',
    image: '/reddit-login-commenters.jpg',
  },
];

const ROTATION_MS = 7000;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ));

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function RedditLoginPage() {
  const { login } = useRedditAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/reddit/dashboard';
  const invited = location.state?.invited;
  const reducedMotion = usePrefersReducedMotion();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [deckPaused, setDeckPaused] = useState(false);

  const slide = SLIDES[slideIndex];

  useEffect(() => {
    const prev = document.title;
    document.title = 'Reddit Dashboard – Sign in';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (deckPaused || reducedMotion) return undefined;
    const timer = window.setInterval(() => {
      setSlideIndex((current) => (current + 1) % SLIDES.length);
    }, ROTATION_MS);
    return () => window.clearInterval(timer);
  }, [deckPaused, reducedMotion, slideIndex]);

  function showSlide(nextIndex) {
    setSlideIndex((nextIndex + SLIDES.length) % SLIDES.length);
  }

  function onDeckKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showSlide(slideIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showSlide(slideIndex - 1);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const d = err.response?.data;
      const detail = typeof d?.detail === 'string'
        ? d.detail
        : Array.isArray(d?.detail)
          ? d.detail[0]
          : d?.non_field_errors?.[0];
      setError(
        detail
        || (err.message === 'Network Error'
          ? 'Cannot reach the API. Is the backend running on port 8000?'
          : null)
        || 'Sign-in failed. Check your email and password.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-auth-page rh-login-page">
      <div className="rh-login-shell">
        <section className="rh-login-form-col">
          <div className="rh-auth-card">
            <div className="rh-login-brand">
              <div className="rh-brand-icon" aria-hidden>R</div>
              <p className="rh-login-product">Reddit Campaign Hub</p>
            </div>
            <h1>Welcome back</h1>
            <p className="rh-login-lede">Sign in to the commenter and admin portal.</p>

            {invited && (
              <p className="rh-login-invited">Password updated. Sign in with your new credentials.</p>
            )}
            {error && <div className="rh-auth-error" role="alert">{error}</div>}

            <form onSubmit={onSubmit} className="rh-auth-form">
              <div className="rh-field">
                <label htmlFor="rh-email">Email</label>
                <input
                  id="rh-email"
                  type="email"
                  autoComplete="username"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="rh-field">
                <div className="rh-login-label-row">
                  <label htmlFor="rh-password">Password</label>
                  <Link className="rh-login-forgot" to="/reddit/forgot-password">
                    Forgot password?
                  </Link>
                </div>
                <div className="rh-login-password">
                  <input
                    id="rh-password"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="rh-login-reveal"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    aria-pressed={showPw}
                  >
                    <EyeIcon open={showPw} />
                  </button>
                </div>
              </div>
              <button type="submit" className="rh-btn-primary" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            <div className="rh-login-portal">
              <Link to="/">Back to portal</Link>
            </div>
          </div>
        </section>

        <aside
          className="rh-login-feature"
          aria-roledescription="carousel"
          aria-label="What Reddit Campaign Hub does"
          onMouseEnter={() => setDeckPaused(true)}
          onMouseLeave={() => setDeckPaused(false)}
          onFocusCapture={() => setDeckPaused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setDeckPaused(false);
          }}
          onKeyDown={onDeckKeyDown}
        >
          <div className="rh-login-stage">
            <img
              key={slide.id}
              className="rh-login-art"
              src={slide.image}
              alt=""
              width="864"
              height="1152"
            />
            <div className="rh-login-caption">
              <p className="rh-login-kicker">{slide.kicker}</p>
              <h2 id={`rh-login-slide-${slide.id}`}>{slide.title}</h2>
              <p>{slide.body}</p>
              <div className="rh-login-controls">
                <button
                  type="button"
                  className="rh-login-nav"
                  aria-label="Previous feature"
                  onClick={() => showSlide(slideIndex - 1)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <div className="rh-login-dots" role="group" aria-label="Feature slides">
                  {SLIDES.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      className={index === slideIndex ? 'rh-login-dot rh-login-dot--active' : 'rh-login-dot'}
                      aria-label={`Show ${item.title}`}
                      aria-current={index === slideIndex ? 'true' : undefined}
                      onClick={() => showSlide(index)}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="rh-login-nav"
                  aria-label="Next feature"
                  onClick={() => showSlide(slideIndex + 1)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
                <span className="rh-login-sr">{`${slideIndex + 1} of ${SLIDES.length}`}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
